import { RouterProvider } from "react-router-dom";
import Router from "./constants/Routing";
import { createContext, useEffect, useState, Suspense } from "react";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { analytics, logEvent } from "./constants/firebase-config";
import English from "./lang/en.json";
import Spanish from "./lang/sp.json";
import FontFaceObserver from "fontfaceobserver";
import { QueryClient, QueryClientProvider } from "react-query";
import MedicalRadarLoader from "./components/common/MedicalRadarLoader";
import ErrorBoundary from "./pages/Error/ErrorBoundary";

const queryClient = new QueryClient();

export const Sponsorpost = createContext();
export const Language = createContext();
export const ScrollCordinates = createContext();
export const LoginUserData = createContext();
export const FeedType = createContext();
export const FeedDataRefetch = createContext();
export const PostDetailContext = createContext();
export const NavigationContext = createContext(); // New context for navigation tracking

function App() {
  const id = JSON.parse(localStorage.getItem("SignupData"))?.data?.id || null;

  const [postImage, setPostImage] = useState("");
  const [postDetail, setPostDetail] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [lang, setLang] = useState(() => {
    const savedLang = localStorage.getItem("selectedLanguage");
    return savedLang === "Spanish" ? Spanish : English;
  });
  const [refetch, setRefetch] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [splashData, setSplashData] = useState({
    user: { id: id },
  });
  const [feedType, setFeedType] = useState(
    () => sessionStorage.getItem("FeedType") || "all"
  );
  const [isAppReady, setIsAppReady] = useState(false);
  const [showLoader, setShowLoader] = useState(true); // Start with loader
  
  // New states for navigation tracking
  const [currentUrl, setCurrentUrl] = useState(window.location.pathname);
  const [previousUrl, setPreviousUrl] = useState(null);
  const [navigationHistory, setNavigationHistory] = useState([]);
  
  const MIN_LOADER_DURATION = process.env.NODE_ENV === "production" ? 2000 : 1000; // 2s in production, 1s in dev

  // Language update function
  const updateLang = (newLanguage) => {
    if (newLanguage === "English") {
      setLang(English);
      localStorage.setItem("selectedLanguage", "English");
    } else if (newLanguage === "Spanish") {
      setLang(Spanish);
      localStorage.setItem("selectedLanguage", "Spanish");
    }
  };

  // Context update functions
  const updateScrollPosition = (newValue) => setScrollPosition(newValue);
  const updateContext = (newValue) => setPostImage(newValue);
  const updateSplash = (newValue) => setSplashData(newValue);
  const updateFeedType = (newValue) => {
    setFeedType(newValue);
    sessionStorage.setItem("FeedType", newValue);
  };
  const updateRefetch = (newValue) => setRefetch(newValue);
  const updatePostDetail = (newValue) => {
    setPostDetail(newValue);
    setIsOpen(true);
  };
  const postModalClose = () => {
    setPostDetail({});
    setIsOpen(false);
  };

  // Navigation tracking functions
  const updateCurrentUrl = (newUrl) => {
    
    // Don't update if it's the same URL
    if (newUrl === currentUrl) return;
    
    // Set previous URL
    setPreviousUrl(currentUrl);
    
    // Update current URL
    setCurrentUrl(newUrl);
    
    // Update navigation history (keep last 10 entries)
    setNavigationHistory(prev => {
      const newHistory = [...prev, { url: newUrl, timestamp: Date.now() }];
      return newHistory.slice(-10); // Keep only last 10 entries
    });
    
    // Store in sessionStorage for persistence across refreshes
    sessionStorage.setItem("currentUrl", newUrl);
    if (currentUrl) {
      sessionStorage.setItem("previousUrl", currentUrl);
    }
  };

  const getReturnUrl = () => {
    // Get the URL to return to after specialization
    // Exclude auth-related pages
    const excludedPaths = ['/login', '/signup', '/register', '/auth', '/specialization'];
    
    // Check previous URL first
    if (previousUrl && !excludedPaths.some(path => previousUrl.toLowerCase().includes(path.toLowerCase()))) {
      return previousUrl;
    }
    
    // Check navigation history for a valid return URL
    const validHistory = navigationHistory
      .filter(entry => !excludedPaths.some(path => entry.url.toLowerCase().includes(path.toLowerCase())))
      .reverse(); // Get most recent first
    
    if (validHistory.length > 0) {
      return validHistory[0].url;
    }
    
    // Default fallback
    return "/feed";
  };

  // Track URL changes
  useEffect(() => {
    const handleLocationChange = () => {
      const newUrl = window.location.pathname;
      updateCurrentUrl(newUrl);
    };

    // Listen for popstate events (back/forward button)
    window.addEventListener('popstate', handleLocationChange);
    
    // Check for URL changes periodically (for programmatic navigation)
    const intervalId = setInterval(() => {
      const newUrl = window.location.pathname;
      if (newUrl !== currentUrl) {
        updateCurrentUrl(newUrl);
      }
    }, 100);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      clearInterval(intervalId);
    };
  }, [currentUrl]);

  // Initialize from sessionStorage on app load
  useEffect(() => {
    const storedCurrentUrl = sessionStorage.getItem("currentUrl");
    const storedPreviousUrl = sessionStorage.getItem("previousUrl");
    
    if (storedCurrentUrl && storedCurrentUrl !== currentUrl) {
      setCurrentUrl(storedCurrentUrl);
    }
    
    if (storedPreviousUrl) {
      setPreviousUrl(storedPreviousUrl);
    }
  }, []);

  // Consolidated font loading and minimum loader time
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Load fonts
        const fonts = [
          "Noto-regular",
          "Noto-medium",
          "Noto-bold",
          "realRoboto-regular",
          "realRoboto-medium",
          "realRoboto-bold",
          "Outfit-regular",
          "Outfit-medium",
          "Outfit-semibold",
          "Outfit-bold",
        ];
        const fontObservers = fonts.map((family) => new FontFaceObserver(family));
        await Promise.all(fontObservers.map((observer) => observer.load(null, 5000)));

        // Enforce minimum initialization delay (e.g., 1 second)
        await new Promise((resolve) => setTimeout(resolve, 1000));

        setIsAppReady(true);
      } catch (error) {
        console.error("App initialization failed:", error);
        setIsAppReady(true); // Proceed even if fonts fail to load
      }
    };

    initializeApp();

    // Enforce minimum loader duration
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, MIN_LOADER_DURATION);

    return () => clearTimeout(timer);
  }, []);

  // Initial setup (timezone, language detection, body class)
  useEffect(() => {
    document.body.classList.add("scroll-hide");
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    sessionStorage.setItem("user_timezone", userTimezone);

    const browserLang = navigator.language.toLowerCase();

    if (browserLang.startsWith("en")) {
      setLang(English);
      localStorage.setItem("selectedLanguage", "English");
    } else if (browserLang.startsWith("es")) {
      setLang(Spanish);
      localStorage.setItem("selectedLanguage", "Spanish");
    } else {
      // Default to English if not en or es
      setLang(English);
      localStorage.setItem("selectedLanguage", "English");
    }
  }, []);

  // Firebase analytics
  useEffect(() => {
    const startTime = performance.now();

    window.addEventListener("load", () => {
      const endTime = performance.now();
      const pageLoadTime = endTime - startTime;
      logEvent(analytics, "page_load_time", { load_time: pageLoadTime });
    });

    logEvent(analytics, "app_loaded", { page_path: window.location.pathname });
  }, []);
  // Show app-level loader during initialization or reload
  if (showLoader) {
    return <MedicalRadarLoader />;
  }

  return (
    <div>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <NavigationContext.Provider value={{ 
            currentUrl, 
            previousUrl, 
            navigationHistory, 
            updateCurrentUrl, 
            getReturnUrl 
          }}>
            <LoginUserData.Provider value={{ splashData, updateSplash, lang, updateLang }}>
              <Language.Provider value={{ lang, updateLang }}>
                <ScrollCordinates.Provider value={{ scrollPosition, updateScrollPosition }}>
                  <Sponsorpost.Provider value={{ postImage, updateContext }}>
                    <FeedType.Provider value={{ feedType, updateFeedType }}>
                      <FeedDataRefetch.Provider value={{ refetch, updateRefetch }}>
                        <PostDetailContext.Provider
                          value={{ postDetail, isOpen, postModalClose, updatePostDetail }}
                        >
                          <ToastContainer
                            position="top-right"
                            autoClose={5000}
                            hideProgressBar={false}
                            newestOnTop={false}
                            closeOnClick
                            rtl={false}
                            pauseOnFocusLoss
                            draggable
                            pauseOnHover
                            theme="dark"
                          />
                          {/* Wrap RouterProvider in Suspense, disable fallback during app init */}
                          <Suspense fallback={isAppReady ? <MedicalRadarLoader /> : null}>
                            <RouterProvider router={Router} />
                          </Suspense>
                        </PostDetailContext.Provider>
                      </FeedDataRefetch.Provider>
                    </FeedType.Provider>
                  </Sponsorpost.Provider>
                </ScrollCordinates.Provider>
              </Language.Provider>
            </LoginUserData.Provider>
          </NavigationContext.Provider>
        </ErrorBoundary>
      </QueryClientProvider>
    </div>
  );
}

export default App;